<template>
    <BaseCombobox
        v-model="selectedProfile"
        v-model:input="searchQuery"
        :options="profiles"
        :key-value="(profile) => profile.id"
        :display-value="(profile) => profile.name ?? ''"
        :input-id="inputId"
        :auto-open="autoOpen"
        :auto-focus="autoFocus"
        :disabled="disabled"
        :placeholder="placeholder"
        :actions="actions"
        :disable-portal="disablePortal"
        :has-more="hasMore"
        :is-loading-more="isLoadingMore"
        @action="emit('action', $event)"
        @load-more="loadMore">
        <template v-if="isFetching || hasNoMatch" #empty>
            <div v-if="isFetching" class="flex gap-2 justify-center items-center p-2" aria-live="polite">
                <LoadingSpinner class="w-4 h-4" aria-hidden="true" />
                <p>Searching...</p>
            </div>
            <slot v-else name="no-match"></slot>
        </template>
        <template #item="{ option }">
            <div class="flex items-center gap-3 p-1">
                <BaseAvatar size="sm" theme="primary" :initials="getFirstLastInitials(option?.name ?? '')" />
                <span class="flex flex-col min-w-0">
                    <span class="body-small-semibold truncate">{{ option?.name }}</span>
                    <span v-if="subtitleFor(option)" class="body-small opacity-75 truncate">{{ subtitleFor(option) }}</span>
                </span>
            </div>
        </template>
        <template #loading-more>
            <LoadingSpinner class="w-4 h-4" aria-hidden="true" />
            <p>Loading more...</p>
        </template>
        <template #action="{ action }">
            <slot name="action" :action="action" :has-no-match="hasNoMatch">{{ action.label }}</slot>
        </template>
    </BaseCombobox>
</template>

<script setup lang="ts">
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseCombobox, { type ComboboxAction } from "~vue/components/BaseCombobox.vue";
import { type PublicProfileSearchSource, usePublicProfileSearch } from "~vue/composables/usePublicProfileSearch";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import type { ExistingPublicProfile } from "~vue/types/profile";
import { getFirstLastInitials } from "~vue/utils/stringUtils";
import { watch } from "vue";

type PublicProfileSearchComboboxProps = {
    initialSearchTerm?: string;
    placeholder?: string;
    autoOpen?: boolean;
    autoFocus?: boolean;
    disabled?: boolean;
    inputId?: string;
    actions?: ComboboxAction[];
    source?: PublicProfileSearchSource;
    // Lets a test reach the popover contents, which reka otherwise teleports out of the mounted tree.
    disablePortal?: boolean;
};

const {
    initialSearchTerm = "",
    placeholder = "Search for a collaboration profile",
    autoOpen = true,
    autoFocus = false,
    disabled = false,
    inputId,
    actions = [],
    source = "chat",
    disablePortal = false,
} = defineProps<PublicProfileSearchComboboxProps>();

const emit = defineEmits<{
    select: [profile: ExistingPublicProfile];
    "search-error": [error: unknown];
    action: [id: string];
    "update:query": [query: string];
}>();

const selectedProfile = defineModel<ExistingPublicProfile | null>({ default: null });

const {
    query: searchQuery,
    profiles,
    error,
    isFetching,
    hasNoMatch,
    hasMore,
    isLoadingMore,
    loadMore,
} = usePublicProfileSearch({
    initialSearchTerm,
    source,
});

// Role and department together are what let a caller tell two same-first-name colleagues apart.
const subtitleFor = (profile: ExistingPublicProfile | undefined) => [profile?.title, profile?.division].filter(Boolean).join(" · ");

// Emit on every change of the fetch error ref: emits the error on failure, and
// emits null when useFetch resets it at the next request — preserving the
// original auto-clear-on-retry behavior in whichever caller renders the notice.
watch(error, (value) => {
    emit("search-error", value);
});

watch(selectedProfile, (profile) => {
    if (profile) {
        emit("select", profile);
    }
});

// The field is editable, so the term the caller should describe or act on is whatever is in it now —
// not the term it was seeded with.
watch(searchQuery, (value) => emit("update:query", value), { immediate: true });
</script>
